import type {IDeferred, IPromise, IQService} from 'angular';
import type {RecordEditationAction, RecordEditationActionFactory} from './record-editation';
import type {Observable} from 'rxjs';
import {EMPTY, merge, Subject} from 'rxjs';
import {states} from '../commons/constants/portaro.constants';
import {ActionAndResponse} from './utils/action-and-response';
import {catchError, filter, first, map, mergeMap, publish, refCount, switchMap, tap} from 'rxjs/operators';
import type {EditableField, EditableFieldType, Field, RecordEditation} from 'typings/portaro.be.types';
import {
    AddEmptyFieldAction,
    PublishAction,
    RemoveAction,
    SaveAction,
    SetFondAction
} from './utils/record-editation-actions';
import {ofCode, ofMain} from './record-filter-utils';
import type EditableFieldTypeDataService from './editable-field-type.data-service';
import {ngAsync} from 'shared/utils/ng-@decorators';
import {getFirstById} from 'shared/utils/array-utils.fp';
import type {ValueEditorBindings} from '../commons/value-editors/kp-value-editor/types';

export default class RecordEditationService {
    private readonly UNCOLLAPSED_FIELDS_COUNT_THRESHOLD: number = 3;
    private isRequestPending = false;

    private savingDeferred: IDeferred<void>;
    private publishingDeferred: IDeferred<void>;
    private removingDeferred: IDeferred<void>;

    private collapsedFields = null;
    private allEditableFieldTypes: EditableFieldType[] = [];

    private isSaving = false;
    private isRemoving = false;
    private isPublishing = false;

    private readonly addEmptyFieldAction$: Subject<RecordEditationAction> = new Subject();
    private readonly addEmptySubfieldAction$: Subject<RecordEditationAction> = new Subject();
    private readonly setFieldValueAction$: Subject<RecordEditationAction> = new Subject();
    private readonly clearFieldValueAction$: Subject<RecordEditationAction> = new Subject();
    private readonly moveSubfieldUpAction$: Subject<RecordEditationAction> = new Subject();
    private readonly moveSubfieldDownAction$: Subject<RecordEditationAction> = new Subject();
    private readonly setFieldIndicatorsAction$: Subject<RecordEditationAction> = new Subject();
    private readonly deleteFieldAction$: Subject<RecordEditationAction> = new Subject();
    private readonly setFondAction$: Subject<RecordEditationAction> = new Subject();
    private readonly removeAction$: Subject<RecordEditationAction> = new Subject();
    private readonly saveAction$: Subject<RecordEditationAction> = new Subject();
    private readonly publishAction$: Subject<RecordEditationAction> = new Subject();

    private readonly action$: Observable<RecordEditationAction>; // stream of all actions combined
    private readonly actionAndResponse$: Observable<ActionAndResponse>; // stream of actions and their corresponding responses

    // hooks for completed actions:
    private readonly saveActionsResponse$: Observable<RecordEditation>;
    private readonly publishActionsResponse$: Observable<RecordEditation>;
    private readonly addEmptyFieldActionsResponse$: Observable<ActionAndResponse>;
    private readonly removeActionResponse$: Observable<RecordEditation>;

    /*@ngInject*/
    constructor(private editationObject: RecordEditation,
                private $q: IQService,
                private recordEditationActionFactory: RecordEditationActionFactory,
                private editableFieldTypeDataService: EditableFieldTypeDataService,
                private focusedFieldTypeId?: string) {

        this.refreshEditableFieldTypes()
            .then(() => this.applyEditationObject(editationObject));

        this.action$ = merge(
            this.addEmptyFieldAction$,
            this.addEmptySubfieldAction$,
            this.setFieldValueAction$,
            this.clearFieldValueAction$,
            this.moveSubfieldUpAction$,
            this.moveSubfieldDownAction$,
            this.setFieldIndicatorsAction$,
            this.deleteFieldAction$,
            this.setFondAction$,
            this.removeAction$,
            this.saveAction$,
            this.publishAction$);

        this.actionAndResponse$ = this.action$.pipe(
            tap(() => this.isRequestPending = true),
            switchMap((action) =>
                action.executeAction(this.editationObject)
                    .pipe( // request data
                        catchError((error) => this.handleRequestError(action, error)),  // catch error caused by request
                        map((response) => new ActionAndResponse(action, response)) // pair response with corresponding action
                    )
            ),
            mergeMap(async (result) => {
                if (result.action instanceof SetFondAction) {
                    await this.refreshEditableFieldTypes();
                }
                return result;
            }),
            mergeMap(async (result) => {
                result.response = await this.processResponse(result.response);
                return result;
            }),
            publish(), // publish() + refCount() transform observable from cold to hot, minimizing number of requests
            refCount(),
        );

        this.saveActionsResponse$ = this.actionAndResponse$.pipe(
            filter((actionAndResponse) => actionAndResponse.action instanceof SaveAction),
            map((actionAndResponse) => actionAndResponse.response)
        );
        this.saveActionsResponse$.subscribe(() => this.onSaveComplete());

        this.publishActionsResponse$ = this.actionAndResponse$.pipe(
            filter((actionAndResponse) => actionAndResponse.action instanceof PublishAction),
            map((actionAndResponse) => actionAndResponse.response)
        );
        this.publishActionsResponse$.subscribe(() => this.onPublishComplete());

        this.removeActionResponse$ = this.actionAndResponse$.pipe(
            filter((actionAndResponse) => actionAndResponse.action instanceof RemoveAction),
            map((actionAndResponse) => actionAndResponse.response)
        );
        this.removeActionResponse$.subscribe(() => this.onRemoveComplete());

        this.addEmptyFieldActionsResponse$ = this.actionAndResponse$.pipe(
            filter((actionAndResponse) => actionAndResponse.action instanceof AddEmptyFieldAction)
        );

        this.addEmptyFieldActionsResponse$.subscribe((actionAndResponse) => {
            if (AddEmptyFieldAction.isAddEmptyFieldAction(actionAndResponse.action)) {
                this.createField(actionAndResponse.action, actionAndResponse.response);
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    @ngAsync()
    private async processResponse(editationObject: RecordEditation): Promise<RecordEditation> {
        this.applyEditationObject(editationObject);
        this.isRequestPending = false;
        return this.editationObject;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private handleRequestError(action: RecordEditationAction, error) {
        if (action instanceof PublishAction) {
            this.isPublishing = false;
        }
        if (action instanceof RemoveAction) {
            this.isRemoving = false;
        }
        if (action instanceof SaveAction) {
            this.isSaving = false;
        }
        this.isRequestPending = false;
        return EMPTY;
    }

    /**
     *
     * @description Check if action can be dispatched or
     * that save/remove/publish action is waiting or being processed
     *
     *
     * @return {boolean} return true if action can be dispatched
     *
     */
    private canActionProceed(): boolean {
        return !this.isPublishing && !this.isRemoving && !this.isSaving;
    }

    private onSaveComplete() {
        this.isSaving = false; // unblock next incoming actions
        this.savingDeferred.resolve(); // resolve promise to notify objects waiting for this action to complete
    }

    private onPublishComplete() {
        this.isPublishing = false; // unblock next incoming actions
        this.publishingDeferred.resolve(); // resolve promise to notify objects waiting for this action to complete
    }

    private onRemoveComplete() {
        this.isRemoving = false; // unblock next incoming actions
        this.removingDeferred.resolve(); // resolve promise to notify objects waiting for this action to complete
    }

    @ngAsync()
    private async refreshEditableFieldTypes() {
        this.allEditableFieldTypes = await this.editableFieldTypeDataService.queryByRecordEditation(this.editationObject.id);
    }

    // vrati vsechna opakovatelna nebo ta, ktera ve fieldContaineru nejsou
    private filterAddableFieldTypes(editableFieldTypes: EditableFieldType[], fieldContainer) {
        return editableFieldTypes.filter((fieldType) => fieldType.repeatable || !fieldContainer.fields.some(ofCode(fieldType.code)));
    }

    private applyEditationObject(editationObject) {
        const collapsedFieldsAreNotFilledYet = this.collapsedFields === null;
        if (collapsedFieldsAreNotFilledYet) {
            this.collapsedFields = {};
        }

        editationObject.usedFieldTypes = this.allEditableFieldTypes.filter((fieldType) => editationObject.fields.some(ofCode(fieldType.code)));
        editationObject.addableFieldTypes = this.filterAddableFieldTypes(this.allEditableFieldTypes, editationObject);
        this.allEditableFieldTypes.forEach((fieldType) => {
            const datafieldsOfThisNumber = editationObject.fields.filter(ofCode(fieldType.code));
            for (const field of datafieldsOfThisNumber) {
                if (field.fields) {
                    field.addableFieldTypes = this.filterAddableFieldTypes(fieldType.subfieldTypes, field);
                }

                this.findAndMovePrimaryField(field);

                if (collapsedFieldsAreNotFilledYet) {
                    this.collapsedFields[field.id] = !field.empty && datafieldsOfThisNumber.length > this.UNCOLLAPSED_FIELDS_COUNT_THRESHOLD && field.fields && field.fields.length > 1;
                }
            }
        });

        this.editationObject = editationObject;
    }

    private findAndMovePrimaryField(field: EditableField): Field | null {
        let primarySubfield = field.fields.find(ofMain());
        if (!primarySubfield) {
            if (field.fields.length === 1) {
                primarySubfield = field.fields[0];
            }
        }
        if (!primarySubfield) {
            return;
        }
        field.primaryField = primarySubfield;
        field.fields = field.fields.filter((f) => f !== primarySubfield);
    }

    private createField(action: AddEmptyFieldAction, response: RecordEditation) {
        if (angular.isDefined(action.getFieldRepetition())) { // posunuti collapsedFields pokud nepridavame pole na konec
            const datafieldsOfThisNumber = response.fields.filter(ofCode(action.getFieldType().code));
            for (let i = datafieldsOfThisNumber.length - 1; i > action.getFieldRepetition(); i--) { // vsechny dalsi pole jsou posunute
                const currentField = datafieldsOfThisNumber[i];
                const previousField = datafieldsOfThisNumber[i - 1];
                this.collapsedFields[currentField.id] = this.collapsedFields[previousField.id];
            }
            const index = `${action.getFieldType().code}#${action.getFieldRepetition()}`;
            this.collapsedFields[index] = false; // aktualne pridane pole je rozbalene
        }
        return response;
    }

    public addEmptyField(fieldType, fieldRepetition?) {
        if (this.canActionProceed()) {
            this.addEmptyFieldAction$.next(this.recordEditationActionFactory.createAddEmptyFieldAction(fieldType, fieldRepetition));
        }

        return this.addEmptyFieldActionsResponse$;
    }

    public addEmptySubfield(parentField, subfieldType) {
        if (this.canActionProceed()) {
            this.addEmptySubfieldAction$.next(this.recordEditationActionFactory.createAddEmptySubFieldAction(parentField, subfieldType));
        }
    }

    public setFieldValue(field) {
        if (this.canActionProceed()) {
            this.setFieldValueAction$.next(this.recordEditationActionFactory.createSetFieldValueAction(field));
        }
    }

    public clearFieldValue(field) {
        if (this.canActionProceed()) {
            this.clearFieldValueAction$.next(this.recordEditationActionFactory.createClearFieldValueAction(field));
        }
    }

    public moveSubfieldUp(field) {
        if (this.canActionProceed()) {
            this.moveSubfieldUpAction$.next(this.recordEditationActionFactory.createMoveSubfieldUpAction(field));
        }
    }

    public moveSubfieldDown(field) {
        if (this.canActionProceed()) {
            this.moveSubfieldDownAction$.next(this.recordEditationActionFactory.createMoveSubfieldDownAction(field));
        }
    }

    public setFieldIndicators(field) {
        if (this.canActionProceed()) {
            this.setFieldIndicatorsAction$.next(this.recordEditationActionFactory.createSetFieldIndicatorsAction(field));
        }
    }

    public deleteField(field) {
        if (this.canActionProceed()) {
            this.deleteFieldAction$.next(this.recordEditationActionFactory.createDeleteFieldAction(field));
        }
    }

    public setFond(fond) {
        if (this.canActionProceed()) {
            this.setFondAction$.next(this.recordEditationActionFactory.createSetFondAction(fond));
        }
    }

    public remove(): IPromise<void> {
        this.isRemoving = true; // block next incoming actions
        if (this.isRequestPending) {
            // wait for pending request to finish and then dispatch action
            this.actionAndResponse$.pipe(first()).subscribe(() => this.removeAction$.next(this.recordEditationActionFactory.createRemoveAction()));
        } else {
            this.removeAction$.next(this.recordEditationActionFactory.createRemoveAction());
        }
        this.removingDeferred = this.$q.defer(); // create deferred object (promise)
        return this.removingDeferred.promise; // return promise
    }

    public save(): IPromise<void> {
        this.isSaving = true; // block next incoming actions
        if (this.isRequestPending) {
            // wait for pending request to finish and then dispatch action
            this.actionAndResponse$.pipe(first()).subscribe(() => this.saveAction$.next(this.recordEditationActionFactory.createSaveAction()));
        } else {
            this.saveAction$.next(this.recordEditationActionFactory.createSaveAction());
        }
        this.savingDeferred = this.$q.defer(); // create deferred object (promise)
        return this.savingDeferred.promise; // return promise
    }

    public publish(): IPromise<void> {
        if (!this.editationObject.draft) {
            throw new Error('Record is not a draft, cannot publish');
        }
        this.isPublishing = true; // block next incoming actions
        if (this.isRequestPending) {
            // wait for pending request to finish and then dispatch action
            this.actionAndResponse$.pipe(first()).subscribe(() => this.publishAction$.next(this.recordEditationActionFactory.createPublishAction()));
        } else {
            this.publishAction$.next(this.recordEditationActionFactory.createPublishAction());
        }
        this.publishingDeferred = this.$q.defer(); // create deferred object (promise)
        return this.publishingDeferred.promise; // return promise
    }

    public getEditableSubfieldTypeById(subfieldTypeId: string): EditableFieldType {
        const allEditableSubfieldsTypes =
            this.allEditableFieldTypes
                .flatMap((editableFieldType) => editableFieldType.subfieldTypes || []);

        const editableSubfieldType = getFirstById<EditableFieldType, string>(subfieldTypeId)(allEditableSubfieldsTypes);

        if (!editableSubfieldType) {
            const errorMessage = `Subfield type ${subfieldTypeId} exists in record but does not exist in editation (probably not defined in styles)`;
            throw new Error(errorMessage);
        }
        return editableSubfieldType;
    }

    public isDraft() {
        return this.editationObject.draft;
    }

    public isDeleted() {
        return this.editationObject.deleted;
    }

    public isRevisionSaved() {
        return this.editationObject.revisionSaved;
    }

    public directoryId() {
        return this.editationObject.directoryId;
    }

    public editation() {
        return this.editationObject;
    }

    public fields() {
        return this.editationObject.fields;
    }

    public addableFieldTypes(): EditableFieldType[] {
        // @ts-ignore believe it is there
        return this.editationObject.addableFieldTypes;
    }

    public text() {
        return this.editationObject.text;
    }

    public state() {
        return this.isRequestPending ? states.PROCESSING : states.NORMAL;
    }

    public fondSelectionEditor(): ValueEditorBindings {
        return this.editationObject.fondSelectionEditor;
    }

    public showIndicators(): boolean {
        return this.editationObject.showIndicators;
    }

    public showFieldIdentifiers(): boolean {
        return this.editationObject.showFieldIdentifiers;
    }

    public isFieldManuallyCollapsible(field) {
        return !field.primaryField && field.fields.length > 1;
    }

    public isFieldCollapsed(field) {
        if (this.showIndicators()) {
            return false;
        }
        if (field.fields.length === 0) {
            return true;
        }
        if (field.fields.length === 1) {
            return false;
        }
        return !!this.collapsedFields[field.id];
    }

    public toggleFieldCollapsed(field) {
        if (this.isFieldManuallyCollapsible(field)) {
            this.collapsedFields[field.id] = !this.isFieldCollapsed(field);
        }
    }

    public isInitiallyEdited(subfield) {
        return this.focusedFieldTypeId === subfield.typeId;
    }
}
